/**
 * Merges the backend CRM ingestion custom fields with the preview-only
 * registry (fields created via Data Model settings / grid Manage Columns in
 * Lovable preview mode). Detail sheets rely on this so custom fields created
 * in preview show up on the People / Company / Deal drill-down cards even
 * when there's no backend workspace to query.
 */

import { useEffect, useState } from "react";
import {
  readPreviewCustomFields,
  subscribePreviewCustomFields,
  type PreviewCustomObjectType,
} from "@/lib/preview-custom-fields";
import { readCustomFieldMeta } from "@/lib/preview-custom-field-meta";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";

export interface PreviewCustomFieldDef {
  id: string;
  key: string;
  label: string;
  type: string;
  crmName: string;
  selectOptions?: string[];
}

export function usePreviewCustomFieldsFor(objectType: PreviewCustomObjectType): PreviewCustomFieldDef[] {
  const workspaceId = useWorkspaceId();
  const [fields, setFields] = useState<PreviewCustomFieldDef[]>([]);

  useEffect(() => {
    const compute = () => {
      const entries = readPreviewCustomFields(workspaceId, objectType);
      const mapped: PreviewCustomFieldDef[] = entries.map(e => {
        const meta = readCustomFieldMeta(workspaceId, objectType, e.id);
        const crmName = e.name || (e.id.startsWith("custom_") ? e.id.slice("custom_".length) : e.id);
        return {
          id: e.id,
          key: e.id.startsWith("custom_") ? e.id : `custom_${crmName}`,
          label: e.label,
          type: meta.type || "text",
          crmName,
          selectOptions: meta.options,
        };
      });
      setFields(mapped);
    };
    compute();
    const unsub = subscribePreviewCustomFields(compute);
    // Also react to storage changes from other tabs / meta updates.
    const onStorage = () => compute();
    window.addEventListener("storage", onStorage);
    return () => {
      unsub();
      window.removeEventListener("storage", onStorage);
    };
  }, [workspaceId, objectType]);

  return fields;
}

export function mergeCustomFieldLists<T extends { key: string }>(
  primary: T[],
  extras: T[],
): T[] {
  const seen = new Set(primary.map(p => p.key));
  const merged = [...primary];
  for (const e of extras) {
    if (!seen.has(e.key)) {
      merged.push(e);
      seen.add(e.key);
    }
  }
  return merged;
}